import { useState } from 'react';
import type { Reward } from '@/core/content/schemas/progression';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useSave, useServices } from '@/ui/services/ServicesContext';
import shell from './CatalogSheet.module.css';
import styles from './MailboxSheet.module.css';
import { useBack } from '@/ui/back/backStack';
import { Icon, type IconName } from '@/ui/components/Icon';
import { Postcard } from '@/ui/components/Postcard';
import { SpotArt } from '@/ui/art/SpotArt';

type Mail = ReturnType<typeof useMail>[number];

const KIND_ICON: Record<Mail['kindForUi'], IconName> = {
  postcard: 'mail',
  postcardAndBox: 'mail',
  special: 'sparkle',
  npcLetter: 'mail',
  miniBox: 'packing',
  sticker: 'stickers',
  coins: 'coin',
};

function useMail() {
  const pending = useSave((s) => s.mailbox.pending);
  const special = useSave((s) => s.mailbox.special);
  return [...special, ...pending];
}

/** What was just opened: a postcard / letter to read, or a one-line summary of what went where. */
type Opened =
  { kind: 'read'; titleKey?: string; bodyKey: string; image?: string } | { kind: 'summary'; text: string };

/** S13 Hòm thư (ux-screens §3.13): one gift a day, kept up to 7, nothing is ever lost. */
export function MailboxSheet({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const { content, dispatch } = useServices();
  const mail = useMail();
  const [opened, setOpened] = useState<Opened | null>(null);
  useBack(onClose);

  const describe = (rewards: readonly Reward[]): string =>
    rewards
      .map((r) => {
        switch (r.type) {
          case 'coins':
            return t('ui.reward.coins', { count: r.amount });
          case 'item':
            return t('ui.mail.item', { count: r.qty, name: t(content.items.get(r.itemId)?.nameKey ?? '') });
          case 'postcard':
            return t('ui.mail.postcard_short');
          case 'letter':
            return t('ui.mail.letter_short', {
              name: t(content.npcs.get(content.letters.get(r.letterId)?.from ?? 'npc_lan')?.nameKey ?? ''),
            });
          case 'sticker':
            return t('ui.mail.sticker');
          default:
            return '';
        }
      })
      .filter(Boolean)
      .join(', ');

  const title = (m: Mail): string => {
    switch (m.kindForUi) {
      case 'postcard':
      case 'postcardAndBox':
      case 'special':
        return t('ui.mail.kind.postcard');
      case 'npcLetter':
        return t('ui.mail.kind.letter');
      case 'miniBox':
        return t('ui.mail.kind.box');
      case 'sticker':
        return t('ui.mail.kind.sticker');
      case 'coins':
        return t('ui.mail.kind.coins');
    }
  };

  const open = (m: Mail) => {
    const r = dispatch({ type: 'claimMail', mailId: m.mailId });
    if (!r.ok) return;
    const postcard = m.rewards.find((x) => x.type === 'postcard');
    const letter = m.rewards.find((x) => x.type === 'letter');
    if (postcard?.type === 'postcard') {
      const def = content.postcards.get(postcard.postcardId);
      if (def) {
        setOpened({ kind: 'read', titleKey: def.titleKey, bodyKey: def.bodyKey, image: def.image });
        return;
      }
    }
    if (letter?.type === 'letter') {
      const def = content.letters.get(letter.letterId);
      if (def) {
        setOpened({ kind: 'read', bodyKey: def.bodyKey });
        return;
      }
    }
    setOpened({ kind: 'summary', text: t('ui.mail.got', { what: describe(m.rewards) }) });
  };

  const openAll = () => {
    const all = mail.flatMap((m) => m.rewards);
    if (!dispatch({ type: 'claimMail', mailId: 'all' }).ok) return;
    setOpened({ kind: 'summary', text: t('ui.mail.got', { what: describe(all) }) });
  };

  return (
    <div
      className={shell.scrim}
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section className={shell.sheet} role="dialog" aria-modal="true" aria-label={t('ui.mail.title')}>
        <div className={shell.grip} aria-hidden="true" />
        <header className={shell.header}>
          <h2 className={shell.title}>{t('ui.mail.title')}</h2>
          <span className={styles.count}>{t('ui.mail.count', { count: mail.length })}</span>
          <button
            type="button"
            className={shell.iconBtn}
            aria-label={t('ui.catalog.close')}
            onClick={onClose}
          >
            <Icon name="close" size={26} />
          </button>
        </header>

        {opened ? (
          <div className={styles.opened} data-kind={opened.kind}>
            {opened.kind === 'read' && opened.image ? (
              <Postcard
                image={opened.image}
                body={t(opened.bodyKey)}
                {...(opened.titleKey ? { title: t(opened.titleKey) } : {})}
              />
            ) : opened.kind === 'read' ? (
              <article className={styles.paper}>
                {opened.titleKey && <h3>{t(opened.titleKey)}</h3>}
                <p>{t(opened.bodyKey)}</p>
              </article>
            ) : (
              <p className={styles.summary}>{opened.text}</p>
            )}
            <button
              type="button"
              className={styles.primary}
              onClick={() => {
                setOpened(null);
              }}
            >
              {t('ui.mail.back')}
            </button>
          </div>
        ) : mail.length === 0 ? (
          <div className={styles.empty}>
            <SpotArt name="mailbox_empty_illus" fallback="mail" size={150} />
            <p>{t('ui.mail.empty')}</p>
          </div>
        ) : (
          <>
            <ul className={styles.list} data-testid="mail-list">
              {mail.map((m) => (
                <li key={m.mailId} className={styles.item}>
                  <span className={styles.icon} data-kind={m.kindForUi} aria-hidden="true">
                    <Icon name={KIND_ICON[m.kindForUi]} size={30} />
                  </span>
                  <span className={styles.label}>
                    <strong>{title(m)}</strong>
                    <small>{describe(m.rewards)}</small>
                  </span>
                  <button
                    type="button"
                    className={styles.open}
                    data-testid={`mail-open-${m.mailId}`}
                    onClick={() => {
                      open(m);
                    }}
                  >
                    {m.kindForUi === 'coins' ? t('ui.mail.claim') : t('ui.mail.open')}
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" className={`${styles.primary} ${styles.all}`} onClick={openAll}>
              {t('ui.mail.open_all')}
            </button>
          </>
        )}
        <p className={styles.footer}>{t('ui.mail.footer', { cap: content.balance.mailboxCap })}</p>
      </section>
    </div>
  );
}
